import { ArrowLeft, Camera } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { message } from "antd";
import welcomeBg from "../../assets/homebg.png";
import ProfileService from "../../services/ProfileService";
import { useQueryClient } from "@tanstack/react-query";

const EditProfilePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage") ?? ""
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "Male",
    dateOfBirth: "",
    heightFeet: "5",
    heightInches: "5",
    heightCm: "170",
    height: "170",
    heightUnit: "cm",
    weight: "55",
    weightUnit: "kg",
    ethnicity: "",
  });

  // Get today's date in YYYY-MM-DD format for max date validation
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Convert API date format (DD/MM/YYYY) to HTML date format (YYYY-MM-DD)
  const convertDateToHTMLFormat = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const [day, month, year] = dateStr.split("/");
      if (!day || !month || !year) {
        return "";
      }
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    } catch (error) {
      // Date conversion failed, return empty string
      return "";
    }
  };

  // Convert HTML date format (YYYY-MM-DD) to API format (DD/MM/YYYY)
  const convertDateToAPIFormat = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const [year, month, day] = dateStr.split("-");
      if (!year || !month || !day) {
        return "";
      }
      return `${day}/${month}/${year}`; // DD/MM/YYYY format
    } catch (error) {
      // Date conversion failed, return empty string
      return "";
    }
  };

  // Map API race values to display values
  const mapRaceFromAPI = (race: string): string => {
    const mapping: { [key: string]: string } = {
      black_african_american: "Black or African American",
      american_indian_alaska_native: "American Indian or Alaska Native",
      native_hawaiian_pacific_islander: "Native Hawaiian/Pacific Islander",
      middle_eastern_north_african: "Middle Eastern or North African",
      white: "White",
      asian: "Asian",
      other: "Other",
    };
    return mapping[race] || "Other";
  };

  // Map display race values to API values
  const mapRaceToAPI = (race: string): string => {
    const mapping: { [key: string]: string } = {
      "Black or African American": "black_african_american",
      "American Indian or Alaska Native": "american_indian_alaska_native",
      "Native Hawaiian/Pacific Islander": "native_hawaiian_pacific_islander",
      "Middle Eastern or North African": "middle_eastern_north_african",
      White: "white",
      Asian: "asian",
      Other: "other",
    };
    return mapping[race] || "other";
  };

  // Load profile data and preferences from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load profile data
        const response = await ProfileService.getEditProfileData();

        if (response?.profile) {
          const profile = response.profile;

          // Use units from profile API response instead of separate preferences call
          const weightUnit = profile.weight_in || "kg";
          const heightUnit = profile.height_in || "cm";

          // Convert weight based on preferred unit
          const weightValue =
            weightUnit === "lbs"
              ? (
                profile.weight_lbs ||
                (profile.weight_kg ? profile.weight_kg * 2.205 : 55)
              ).toString()
              : (
                profile.weight_kg ||
                (profile.weight_lbs ? profile.weight_lbs / 2.205 : 55)
              ).toString();

          // Convert height based on preferred unit
          let heightValue = "";
          let heightFeet = profile.height_ft?.toString() || "5";
          let heightInches = profile.height_inches?.toString() || "5";
          let heightCm = profile.height_cm?.toString() || "170";

          if (heightUnit === "ft_in") {
            // If preference is ft_in, use feet/inches
            if (profile.height_cm) {
              const totalInches = profile.height_cm / 2.54;
              heightFeet = Math.floor(totalInches / 12).toString();
              heightInches = Math.round(totalInches % 12).toString();
            }
            heightValue = `${heightFeet}'${heightInches}"`;
          } else {
            // If preference is cm, convert feet/inches to cm if needed
            if (profile.height_ft && profile.height_inches) {
              heightCm = (
                (profile.height_ft * 12 + profile.height_inches) *
                2.54
              ).toString();
            }
            heightValue = heightCm;
          }

          setFormData({
            name: profile.full_name || "",
            email: profile.email || "",
            gender: profile.gender
              ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1)
              : "Male",
            dateOfBirth: convertDateToHTMLFormat(profile.date_of_birth || ""),
            heightFeet: heightFeet,
            heightInches: heightInches,
            heightCm: heightCm,
            height: heightValue,
            heightUnit: heightUnit,
            weight: parseFloat(weightValue).toFixed(1),
            weightUnit: weightUnit,
            ethnicity: mapRaceFromAPI(profile.race || ""),
          });

          if (profile.photo) {
            setProfileImage(profile.photo);
          }
        }
      } catch (error) {
        message.error("Error loading data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        message.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setProfileImage(result);
        localStorage.setItem("profileImage", result);
        // message.success("Profile photo updated!");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare API payload - Don't send photo field (API doesn't support image upload via edit profile)
      // Photo updates should be handled separately if API provides that endpoint
      const profilePayload: any = {
        full_name: formData.name,
        email: formData.email,
        gender: formData.gender.toLowerCase(),
        date_of_birth: convertDateToAPIFormat(formData.dateOfBirth),
        height_in: formData.heightUnit, // Use user's preferred height unit
        // Send height values based on preferred unit - set others to null
        height_ft:
          formData.heightUnit === "ft_in"
            ? parseInt(formData.heightFeet) || 0
            : null,
        height_inches:
          formData.heightUnit === "ft_in"
            ? parseInt(formData.heightInches) || 0
            : null,
        height_cm:
          formData.heightUnit === "cm"
            ? parseInt(formData.heightCm) || 0
            : null,
        weight_in: formData.weightUnit,
        // Send weight values based on preferred unit - set other to null
        weight_kg:
          formData.weightUnit === "kg"
            ? parseFloat(formData.weight) || 0
            : null,
        weight_lbs:
          formData.weightUnit === "lbs"
            ? parseFloat(formData.weight) || 0
            : null,
        race: mapRaceToAPI(formData.ethnicity),
      };

      // Only include photo if it's a valid UUID (not a URL or base64)
      // Skip photo field entirely - API doesn't support photo upload in edit profile
      // Photo should be uploaded separately if needed

      await ProfileService.updateProfile(profilePayload);

      // Also save to localStorage as backup
      localStorage.setItem("userProfile", JSON.stringify(formData));

      // Invalidate home page data to trigger refresh with new BMI data
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });

      // message.success("Profile updated successfully!");
      navigate("/glucera/profilepage");
    } catch (error) {
      message.error("Error saving profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
              <p className="text-muted-foreground">Loading profile...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/glucera/profilepage")}
                className="rounded-xl bg-card/80 hover:bg-card"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold text-foreground">
                Edit Profile
              </h1>
              <div className="w-10" />
            </div>

            {/* Profile Picture */}
            <Card className="rounded-2xl border border-border bg-card p-6 shadow-lg">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="mb-4 h-24 w-24 overflow-hidden rounded-full border-4 border-blue-100 bg-gradient-to-br from-blue-500 to-purple-600 dark:border-blue-900">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-white">
                        {formData.name
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")}
                      </div>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-border bg-card hover:bg-muted"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Change Photo
                </Button>
              </div>
            </Card>

            {/* Personal Information */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 font-semibold text-foreground">
                Personal Information
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-muted-foreground">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={e =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="rounded-xl border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-muted-foreground">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="rounded-xl border-border"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="gender" className="text-muted-foreground">
                      Gender
                    </Label>
                    <div className="relative">
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={e =>
                          setFormData({ ...formData, gender: e.target.value })
                        }
                        className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="dateOfBirth"
                      className="text-muted-foreground"
                    >
                      Date of Birth
                    </Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      max={getTodayDate()}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          dateOfBirth: e.target.value,
                        })
                      }
                      className="rounded-xl border-border px-4 py-2 text-sm"
                      style={{ position: "relative" }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Height ({formData.heightUnit === "cm" ? "Cm" : "Ft-Inch"})
                  </Label>
                  {formData.heightUnit === "cm" ? (
                    <Input
                      type="number"
                      value={formData.heightCm}
                      onChange={e => {
                        const cmValue = e.target.value;
                        const totalInches = parseFloat(cmValue) / 2.54;
                        const feet = Math.floor(totalInches / 12).toString();
                        const inches = Math.round(totalInches % 12).toString();

                        setFormData({
                          ...formData,
                          heightCm: cmValue,
                          heightFeet: feet,
                          heightInches: inches,
                          height: cmValue,
                        });
                      }}
                      placeholder="170"
                      className="rounded-xl border-border"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="number"
                        value={formData.heightFeet}
                        onChange={e => {
                          const feetValue = e.target.value;
                          const cmValue = (
                            (parseFloat(feetValue) * 12 +
                              parseFloat(formData.heightInches)) *
                            2.54
                          ).toString();

                          setFormData({
                            ...formData,
                            heightFeet: feetValue,
                            heightCm: cmValue,
                            height: `${feetValue}'${formData.heightInches}"`,
                          });
                        }}
                        placeholder="Feet"
                        className="rounded-xl border-border"
                      />
                      <Input
                        type="number"
                        value={formData.heightInches}
                        onChange={e => {
                          const inchesValue = e.target.value;
                          const cmValue = (
                            (parseFloat(formData.heightFeet) * 12 +
                              parseFloat(inchesValue)) *
                            2.54
                          ).toString();

                          setFormData({
                            ...formData,
                            heightInches: inchesValue,
                            heightCm: cmValue,
                            height: `${formData.heightFeet}'${inchesValue}"`,
                          });
                        }}
                        placeholder="Inches"
                        className="rounded-xl border-border"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Weight ({formData.weightUnit === "kg" ? "Kg" : "Lbs"})
                  </Label>
                  <Input
                    type="number"
                    value={formData.weight}
                    onChange={e =>
                      setFormData({ ...formData, weight: e.target.value })
                    }
                    placeholder={formData.weightUnit === "kg" ? "68" : "150"}
                    className="rounded-xl border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ethnicity" className="text-muted-foreground">
                    Race/Ethnicity
                  </Label>
                  <div className="relative">
                    <select
                      id="ethnicity"
                      value={formData.ethnicity}
                      onChange={e =>
                        setFormData({ ...formData, ethnicity: e.target.value })
                      }
                      className="flex h-10 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="White">White</option>
                      <option value="Black or African American">
                        Black or African American
                      </option>
                      <option value="Asian">Asian</option>
                      <option value="American Indian or Alaska Native">
                        American Indian or Alaska Native
                      </option>
                      <option value="Native Hawaiian/Pacific Islander">
                        Native Hawaiian/Pacific Islander
                      </option>
                      <option value="Middle Eastern or North African">
                        Middle Eastern or North African
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              className="h-12 w-full rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditProfilePage;
